import React, { useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, Layers, CheckCircle2, XCircle, RotateCw, ExternalLink,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import { Button, Pill, Card, EmptyState, Slideout } from '../../index';
import { Pagination, usePaginationState } from '../../../common/Pagination';
import { StatusBadge } from '../widgets/StatusBadge';
import {
  ccListCanonicalControls, ccGetFrameworkMappingStatuses,
  ccTriggerMappingScan, ccApproveMapping, ccRejectMapping,
  ccGetPendingSuggestions, ccVerifyMapping,
} from '../../../../services/api';

/**
 * ControlMappingSlideout — V2 Control Mapping (Phase B3 / G316).
 *
 * Replaces ?legacy=1&tab=CONTROL_MAPPING. Production rebuild of the
 * canonical-control alignment workspace:
 *
 *   - Two sub-views:
 *       Canonical controls (left): unified Cloud Control Canonical list
 *       Framework mappings (right): per-framework approval queue
 *   - Trigger mapping scan per framework (Temporal OSS job)
 *   - Approve / Reject pending suggestions
 *   - Verify a mapping's integrity (hash chain)
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.addToast
 */
const VIEWS = [
  { id: 'canonical', label: 'Canonical controls' },
  { id: 'pending',   label: 'Pending approval' },
  { id: 'frameworks', label: 'Framework status' },
];

export function ControlMappingSlideout({ isOpen, onClose, addToast }) {
  const [view, setView] = useState('pending');
  const [canonical, setCanonical] = useState([]);
  const [pending, setPending] = useState([]);
  const [frameworkStatuses, setFrameworkStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const pg = usePaginationState(20);

  const reload = async () => {
    setLoading(true);
    try {
      const [cR, pR, fR] = await Promise.all([
        ccListCanonicalControls().catch(() => null),
        ccGetPendingSuggestions().catch(() => null),
        ccGetFrameworkMappingStatuses().catch(() => null),
      ]);
      setCanonical(normalizeList(cR));
      setPending(normalizeList(pR));
      setFrameworkStatuses(normalizeList(fR));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (isOpen) reload(); /* eslint-disable-next-line */ }, [isOpen]);
  useEffect(() => { pg.setPage(0); /* eslint-disable-next-line */ }, [view]);

  const activeList = view === 'canonical' ? canonical
                   : view === 'pending'   ? pending
                   :                        frameworkStatuses;
  const visible = pg.paginate(activeList);

  const toggleExpand = (id) => setExpanded((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={820}
      ariaLabel="Control mapping workspace"
      header={
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Layers size={18} className="text-v2-action" />
            <h2 className="text-[16px] font-bold text-v2-ink m-0">Control mapping</h2>
            <Pill tone="cyan" size="sm">{pending.length} pending</Pill>
          </div>
          <p className="text-[12.5px] text-v2-ink-secondary m-0 mb-3">
            Canonical (cloud-control-matrix) controls aligned to framework requirements.
            Approve auto-suggested mappings, trigger fresh scans, verify hash integrity.
          </p>
          <div className="flex items-center gap-1 border-b border-v2-border-subtle -mx-4 px-4">
            {VIEWS.map((v) => {
              const active = view === v.id;
              const count = v.id === 'canonical' ? canonical.length
                          : v.id === 'pending'   ? pending.length
                          :                        frameworkStatuses.length;
              return (
                <button
                  key={v.id}
                  onClick={() => setView(v.id)}
                  aria-selected={active}
                  className={`px-3 py-2 text-[12.5px] font-medium border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${
                    active
                      ? 'border-v2-action text-v2-action'
                      : 'border-transparent text-v2-ink-muted hover:text-v2-ink hover:border-v2-border'
                  }`}
                >
                  {v.label}
                  {count > 0 && (
                    <span className={`text-[10.5px] font-mono-v2 px-1.5 rounded-v2-pill ${
                      active ? 'bg-v2-action text-white' : 'bg-v2-muted text-v2-ink-muted'
                    }`}>{count}</span>
                  )}
                </button>
              );
            })}
            <Button variant="ghost" size="sm"
                    className="ml-auto"
                    iconLeft={<RefreshCw size={12} className={loading ? 'animate-spin' : ''} />}
                    onClick={reload}>
              Refresh
            </Button>
          </div>
        </div>
      }
    >
      {loading && activeList.length === 0 && (
        <p className="text-[12.5px] text-v2-ink-muted">Loading…</p>
      )}

      {/* Canonical Controls view */}
      {view === 'canonical' && !loading && (
        activeList.length === 0 ? (
          <EmptyState icon={<Layers size={20} />} title="No canonical controls"
                      description="The Cloud Control Canonical catalogue is empty for this tenant." />
        ) : (
          <ul className="space-y-1.5 m-0 p-0 list-none">
            {visible.map((c) => (
              <li key={c.id || c.controlId} className="bg-v2-surface border border-v2-border-subtle rounded-v2-sm p-3 flex items-start gap-3">
                <Pill tone="cyan" size="sm">{c.controlId || c.id}</Pill>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-v2-ink text-[13px]">{c.name || c.title}</div>
                  <div className="text-[11.5px] text-v2-ink-muted">{c.description}</div>
                </div>
                {c.mappingCount != null && (
                  <Pill tone="muted" size="sm">{c.mappingCount} mappings</Pill>
                )}
              </li>
            ))}
          </ul>
        )
      )}

      {/* Pending Suggestions view */}
      {view === 'pending' && !loading && (
        activeList.length === 0 ? (
          <EmptyState icon={<CheckCircle2 size={20} />} title="No pending mappings"
                      description="All auto-suggested mappings have been approved or rejected." />
        ) : (
          <ul className="space-y-2 m-0 p-0 list-none">
            {visible.map((s) => (
              <PendingRow
                key={s.id}
                suggestion={s}
                expanded={expanded.has(s.id)}
                onToggle={() => toggleExpand(s.id)}
                onApprove={async () => {
                  setBusy(s.id);
                  try {
                    const r = await ccApproveMapping(s.id);
                    if (r?.ok === false) throw new Error(r.error || 'Failed');
                    addToast?.('Mapping approved.', 'success');
                    reload();
                  } catch (e) { addToast?.(`Failed: ${e?.message}`, 'error'); }
                  finally { setBusy(null); }
                }}
                onReject={async () => {
                  setBusy(s.id);
                  try {
                    const r = await ccRejectMapping(s.id);
                    if (r?.ok === false) throw new Error(r.error || 'Failed');
                    addToast?.('Mapping rejected.', 'warn');
                    reload();
                  } catch (e) { addToast?.(`Failed: ${e?.message}`, 'error'); }
                  finally { setBusy(null); }
                }}
                onVerify={async () => {
                  setBusy(s.id);
                  try {
                    const r = await ccVerifyMapping(s.id);
                    const valid = r?.data?.valid ?? r?.valid;
                    if (r?.ok === false) addToast?.(`Verify failed: ${r.error}`, 'error');
                    else if (valid === false) addToast?.('⚠ Mapping integrity BROKEN.', 'error');
                    else addToast?.('Mapping verified.', 'success');
                  } finally { setBusy(null); }
                }}
                disabled={busy === s.id}
              />
            ))}
          </ul>
        )
      )}

      {/* Framework Status view */}
      {view === 'frameworks' && !loading && (
        activeList.length === 0 ? (
          <EmptyState icon={<Layers size={20} />} title="No frameworks configured"
                      description="Enable a framework in Policy Config first." />
        ) : (
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b border-v2-border-subtle">
                <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Framework</th>
                <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Mappings</th>
                <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Pending</th>
                <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Coverage</th>
                <th className="text-right text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((f) => {
                const pct = f.coveragePct ?? f.coverage ?? 0;
                return (
                  <tr key={f.id || f.frameworkId} className="border-b border-v2-border-subtle/50">
                    <td className="py-2">
                      <div className="font-semibold text-v2-ink">{f.name || f.frameworkName}</div>
                      <div className="text-[10.5px] text-v2-ink-muted">{f.frameworkId || f.id}</div>
                    </td>
                    <td className="py-2 font-mono-v2 text-[12px]">{f.totalMappings ?? f.mappingCount ?? '—'}</td>
                    <td className="py-2">
                      {f.pendingCount > 0 ? (
                        <Pill tone="warning" size="sm">{f.pendingCount}</Pill>
                      ) : <span className="text-v2-ink-muted text-[11px]">—</span>}
                    </td>
                    <td className="py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-v2-muted rounded-v2-pill overflow-hidden">
                          <div className={`h-full ${pct >= 80 ? 'bg-v2-success' : pct >= 60 ? 'bg-v2-warning' : 'bg-v2-danger'}`}
                               style={{ width: `${pct}%` }} />
                        </div>
                        <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{pct}%</span>
                      </div>
                    </td>
                    <td className="py-2 text-right">
                      <Button variant="ghost" size="sm" iconLeft={<RotateCw size={11} />}
                              disabled={busy === f.id}
                              onClick={async () => {
                                setBusy(f.id);
                                try {
                                  const r = await ccTriggerMappingScan(f.id || f.frameworkId);
                                  if (r?.ok === false) throw new Error(r.error);
                                  addToast?.('Mapping scan kicked off — refresh in a minute.', 'success');
                                } catch (e) { addToast?.(`Scan failed: ${e?.message}`, 'error'); }
                                finally { setBusy(null); }
                              }}>
                        Trigger scan
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )
      )}

      {activeList.length > 0 && (
        <div className="mt-3">
          <Pagination
            totalItems={activeList.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun={view === 'frameworks' ? 'frameworks' : 'controls'}
          />
        </div>
      )}
    </Slideout>
  );
}

function PendingRow({ suggestion: s, expanded, onToggle, onApprove, onReject, onVerify, disabled }) {
  const confidence = s.confidence != null ? Math.round(s.confidence * 100) : null;
  return (
    <li className="bg-v2-surface border border-v2-border-subtle rounded-v2-md overflow-hidden">
      <button onClick={onToggle}
              className="w-full text-left px-3 py-2.5 hover:bg-v2-muted flex items-center gap-3">
        {expanded ? <ChevronDown size={14} className="text-v2-ink-muted shrink-0" />
                  : <ChevronRight size={14} className="text-v2-ink-muted shrink-0" />}
        <Pill tone="cyan" size="sm">{s.canonicalControlId || s.canonical?.id}</Pill>
        <span className="text-v2-ink-muted">↔</span>
        <Pill tone="trust" size="sm">{s.frameworkControlId || s.frameworkControl?.id}</Pill>
        <div className="flex-1 min-w-0 text-[12px] text-v2-ink-muted truncate">
          {s.framework || s.frameworkName} · suggested {s.suggestedAt ? new Date(s.suggestedAt).toLocaleDateString() : '—'}
        </div>
        {confidence != null && (
          <Pill tone={confidence >= 80 ? 'success' : confidence >= 60 ? 'warning' : 'muted'} size="sm">
            {confidence}% confidence
          </Pill>
        )}
        <StatusBadge status={s.status || 'PENDING'} />
      </button>

      {expanded && (
        <div className="px-4 pb-3 pt-1 border-t border-v2-border-subtle bg-v2-muted/30">
          {s.reasoning && (
            <div className="mt-3 mb-3">
              <span className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted block mb-1">
                Reasoning
              </span>
              <p className="text-[12.5px] text-v2-ink m-0">{s.reasoning}</p>
            </div>
          )}
          <div className="flex items-center gap-2 pt-2 border-t border-v2-border-subtle">
            <Button variant="primary" size="sm" iconLeft={<CheckCircle2 size={13} />}
                    disabled={disabled} onClick={onApprove}>
              Approve
            </Button>
            <Button variant="danger" size="sm" iconLeft={<XCircle size={13} />}
                    disabled={disabled} onClick={onReject}>
              Reject
            </Button>
            <Button variant="ghost" size="sm" iconLeft={<ExternalLink size={11} />}
                    disabled={disabled} onClick={onVerify} className="ml-auto">
              Verify integrity
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

function normalizeList(r) {
  if (!r) return [];
  if (Array.isArray(r)) return r;
  return r?.data || r?.items || r?.content || r?.suggestions || r?.mappings || [];
}

export default ControlMappingSlideout;
